import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { signatureAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useSite } from '../../context/SiteContext';

// ── Canvas de signature ──────────────────────────────────────
const SignatureCanvas = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw  = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); const p = getPos(e, canvasRef.current); ctx.moveTo(p.x, p.y); };
  const draw       = (e) => { e.preventDefault(); if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); const p = getPos(e, canvasRef.current); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke(); };
  const stopDraw   = () => {
    drawing.current = false;
    setHasSignature(true);
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ position: 'relative', border: '2px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', overflow: 'hidden', cursor: 'crosshair', touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          width={560}
          height={180}
          style={{ display: 'block', width: '100%', height: 180 }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {!hasSignature && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ color: '#94a3b8', fontSize: 14 }}>✍️ Signez ici en maintenant le clic ou en glissant le doigt</span>
          </div>
        )}
      </div>
      {hasSignature && (
        <button onClick={clear} style={{ alignSelf: 'flex-end', padding: '4px 12px', fontSize: 12, color: '#dc2626', background: 'none', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}>
          🗑 Effacer
        </button>
      )}
    </div>
  );
};

// ── Page publique de signature ───────────────────────────────
const SignaturePage = () => {
  const { token }    = useParams();
  const [data,       setData]      = useState(null);
  const [loading,    setLoading]   = useState(true);
  const [error,      setError]     = useState('');
  const [signature,  setSignature] = useState(null);
  const [accepted,   setAccepted]  = useState(false);
  const [submitting, setSubmitting]= useState(false);
  const [signed,     setSigned]    = useState(false);
  const [proofHash,  setProofHash] = useState('');
  const { settings } = useSite();

  useEffect(() => {
    signatureAPI.getSignPage(token)
      .then(res => setData(res.data.data))
      .catch(err => setError(err.response?.data?.message || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!signature) { toast.error('Veuillez apposer votre signature'); return; }
    if (!accepted)  { toast.error('Vous devez accepter les conditions'); return; }
    setSubmitting(true);
    try {
      const res = await signatureAPI.submitSign(token, { signature_data: signature, accepted: true });
      setProofHash(res.data.data?.proof_hash || '');
      setSigned(true);
      toast.success('Devis signé avec succès ✓');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la signature');
    } finally {
      setSubmitting(false);
    }
  };

  // ── États d'affichage ──
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 24 }}>
      <div style={{ maxWidth: 500, textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>Lien invalide</h2>
        <p style={{ color: '#64748b' }}>{error}</p>
      </div>
    </div>
  );

  if (signed) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4', padding: 24 }}>
      <div style={{ maxWidth: 560, width: '100%', background: '#fff', borderRadius: 16, padding: 40, boxShadow: '0 4px 24px rgba(0,0,0,.08)', textAlign: 'center' }}>
        <div style={{ fontSize: 72, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: '#16a34a', marginBottom: 8 }}>Devis signé !</h2>
        <p style={{ color: '#64748b', marginBottom: 24 }}>Votre signature a été enregistrée. Nous vous contacterons prochainement.</p>
        <div style={{ background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: 10, padding: '16px 20px', textAlign: 'left' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 8 }}>🔐 Preuve de signature</div>
          <div style={{ fontSize: 12, color: '#166534', fontFamily: 'monospace', wordBreak: 'break-all' }}>{proofHash}</div>
        </div>
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 20 }}>Un email de confirmation vous a été envoyé.</p>
      </div>
    </div>
  );

  const items    = data?.items    || [];
  const total    = parseFloat(data?.total_ttc) || 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '32px 16px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#1e293b', letterSpacing: -1 }}>
          {settings.site_name}
          </div>
          <div style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>Signature électronique sécurisée</div>
        </div>

        {/* Devis */}
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,.06)', marginBottom: 20 }}>
          <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>Devis {data.reference}</div>
              <div style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>Bonjour {data.client_name},</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#e63946' }}>{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>TTC</div>
            </div>
          </div>

          {/* Lignes */}
          {items.length > 0 && (
            <div style={{ padding: '16px 28px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Description</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Qté</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => {
                    const total = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0) * (1 - (parseFloat(item.discount) || 0) / 100);
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 10px' }}>{item.description}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'center', color: '#64748b' }}>{item.quantity}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 600 }}>{total.toFixed(2)} €</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Totaux */}
          <div style={{ padding: '16px 28px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: 260, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#64748b' }}>
                <span>Sous-total HT</span><span>{parseFloat(data.subtotal_ht || 0).toFixed(2)} €</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#64748b' }}>
                <span>TVA ({data.tax_rate || 20}%)</span><span>{parseFloat(data.tax_amount || 0).toFixed(2)} €</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 800, fontSize: 16, borderTop: '2px solid #1e293b', marginTop: 4 }}>
                <span>Total TTC</span><span style={{ color: '#e63946' }}>{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
              </div>
            </div>
          </div>
        </div>

        {/* Zone signature */}
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,.06)', padding: 28, marginBottom: 20 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>✍️ Votre signature</h3>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Signez dans le cadre ci-dessous pour valider ce devis.</p>
          <SignatureCanvas onChange={setSignature} />
        </div>

        {/* Acceptation + validation */}
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,.06)', padding: 28 }}>
          <label style={{ display: 'flex', gap: 12, cursor: 'pointer', marginBottom: 20 }}>
            <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} style={{ marginTop: 2, width: 18, height: 18, accentColor: '#e63946', cursor: 'pointer', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
              En signant électroniquement ce document, j'accepte les conditions du devis <strong>{data.reference}</strong> pour un montant de <strong>{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € TTC</strong> et j'autorise {settings.site_name} à procéder a l'execution de ma commande.
            </span>
          </label>

          <button
            onClick={submit}
            disabled={submitting || !signature || !accepted}
            style={{
              width: '100%', padding: '16px', fontSize: 16, fontWeight: 800,
              background: signature && accepted ? '#e63946' : '#e2e8f0',
              color: signature && accepted ? '#fff' : '#94a3b8',
              border: 'none', borderRadius: 10, cursor: signature && accepted ? 'pointer' : 'not-allowed',
              transition: 'all .2s',
            }}
          >
            {submitting ? '⏳ Signature en cours…' : '✅ Signer et valider le devis'}
          </button>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 16 }}>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>🔒 Signature sécurisée — horodatée et liée à votre adresse IP</span>
          </div>

          <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
            Valide jusqu'au {new Date(data.expires_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SignaturePage;